import {ActionDefinitionObject} from '../../types/ActionDefinition';
import {ActionResponseBody} from '../../types/api/definition';
import {EagGridState} from '../createEagGridStore';
import {useEagGridSelector} from '../useEagGridSelector';
import {getActions} from './getActions';

// WeakMap keyed on the identity of definition.actions array. When setDefinition()
// is called, the array reference changes → cache miss → recompute.
//
// MUST be a per-input-array WeakMap, NOT a single-slot last-value cache: multiple
// grid instances (e.g. a page grid plus a modal grid) share this module and hold
// DIFFERENT actions arrays. With a single slot, each instance's getSnapshot
// evicts the other's entry, so both snapshots change on every check and React's
// useSyncExternalStore force-rerenders in an endless ping-pong ("Maximum update
// depth exceeded", T20-135733).
const cache = new WeakMap<ActionResponseBody[], ActionDefinitionObject>();

const selectActionsDefinition = (state: EagGridState): ActionDefinitionObject | null => {
  const actions = state.definition?.actions ?? null;

  if (actions === null) {
    return null;
  }

  const cached = cache.get(actions);
  if (cached !== undefined) {
    return cached; // stable reference — useSyncExternalStore skips re-render
  }

  const result = getActions(actions);
  cache.set(actions, result);
  return result;
};

/**
 * Returns the ActionDefinitionObject derived from the grid definition in store.
 * Reference-stable: returns the same object until definition changes.
 * Consumers re-render only when the definition is replaced via setDefinition().
 */
export function useActionsDefinition(): ActionDefinitionObject | null {
  return useEagGridSelector(selectActionsDefinition);
}
